import { Input, Output, OnInit, OnChanges, SimpleChange, SimpleChanges, OnDestroy, EventEmitter, ChangeDetectorRef, Directive } from '@angular/core';
import { Observable, Subject, Subscription } from 'rxjs/Rx';
import { Changeable } from 'common';
import { MessageService } from 'base';
import { MonitorService } from 'monitor';
import { IdentityService } from 'identity';
import { MonitorPolicyEditor } from 'DiscoveryEngine.TS/Monitors';
import { AgentMonitorPolicyDto, UpdateResultDto } from 'UvExplorerServer.Dtos.TS';
import { NavigationService } from 'netview';
import { MonitorConfigComponentBase } from './MonitorConfigComponentBase';


@Directive()
export abstract class MonitorEditorComponentBase implements Changeable, OnInit, OnChanges, OnDestroy {

	@Input()
	public visible: boolean;

	@Input()
	public target: AgentMonitorPolicyDto;

	@Output()
	public visibleChange: EventEmitter<boolean>;

	@Output()
	public onSave: EventEmitter<any>

	@Output()
	public onCancel: EventEmitter<any>;

	protected children: Changeable[];

	public policyEditor: MonitorPolicyEditor;

	constructor(protected monitorService: MonitorService,
		protected messageService: MessageService,
		protected identityService: IdentityService,
		protected navService: NavigationService,
		protected changeDetector: ChangeDetectorRef) {

		this.visible = false;
		this.target = null;
		this.visibleChange = new EventEmitter<boolean>();
		this.onSave = new EventEmitter<any>();
		this.onCancel = new EventEmitter<any>();
		this.children = [];
		this.policyEditor = null;
	}


	public ngOnInit(): void {
		return;
	}

	public ngOnChanges(changes: SimpleChanges): void {
		let change: SimpleChange = null;

		change = changes["target"];
		if (change) {
			if (this.target) {
				this.policyEditor = new MonitorPolicyEditor(this.target.MonitorPolicy);
			}
			else {
				this.policyEditor = null;
			}
		}

		change = changes["visible"];
		if (change) {
			if (this.visible) {
				this.children.forEach((child: Changeable) => {
					(<MonitorConfigComponentBase>child).BuildForm();
				});
			}
		}
	}

	public ngOnDestroy(): void {
		return;
	}

	public show(): void {
		this.visible = true;
		this.visibleChange.emit(true);

		this.changeDetector.markForCheck();
	}

	public hide(): void {
		this.visible = false;
		this.visibleChange.emit(false);

		this.changeDetector.markForCheck();
	}

	//
	// Changeable
	//

	public get tempChanges(): boolean {
		return this.children.some((child: Changeable) => child.tempChanges, this);
	}

	public get tempChangesValid(): boolean {
		return this.children.every((child: Changeable) => {
			if (child.tempChanges) {
				return child.tempChangesValid;
			}
			else {
				return true;
			}
		}, this);
	}

	public get invalidTempChanges(): string[] {
		let result = [];

		this.children.forEach((child: Changeable) => {
			if (child.tempChanges && !child.tempChangesValid) {
				Array.prototype.push.apply(result, child.invalidTempChanges);
			}
		}, this);

		return result;
	}

	// PRE: tempChanges && tempChangesValid
	public applyTempChanges(): void {
		this.children.forEach((child: Changeable) => {
			if (child.tempChanges) {
				if (child.tempChangesValid) {
					child.applyTempChanges();
				}
				else {
					console.assert(false, "applyTempChanges called in invalid state");
				}
			}
		}, this);
	}

	public applyTempChangesIfValid(): void {
		if (this.tempChanges) {
			if (this.tempChangesValid) {
				this.applyTempChanges();
			}
		}
	}

	// PRE: tempChanges
	public discardTempChanges(): void {
		this.children.forEach((child: Changeable) => {
			if (child.tempChanges) {
				child.discardTempChanges();
			}
		}, this);
	}

	public showInvalidTempChangesDialog: boolean = false;
	public tabsWithErrors: string[] = [];

	public onSaveClicked(): void {
		if (this.tempChanges) {

			if (!this.tempChangesValid) {
				// There are invalid temp changes in one or more tabs

				this.tabsWithErrors = this.invalidTempChanges;
				this.showInvalidTempChangesDialog = true;

				return;
			}
			else {
				this.applyTempChanges();
			}
		}

		this.doSaveActions();
		this.policyEditor.CommitChanges();

		this.save().subscribe(
			(result: boolean) => {
				this.onSave.emit();
				this.hide();
			},
			(error) => {
				this.messageService.displayErrorMessage("Could not save monitor changes");
			});
	}

	// Allows subclasses to perform custom save actions.
	protected doSaveActions(): void {
		return;
	}

	public onCancelClicked(): void {
		if (this.tempChanges) {
			this.discardTempChanges();
		}
		this.onCancel.emit();
		this.hide();
	}

	public onCancelSave(): void {
		this.showInvalidTempChangesDialog = false;
		this.tabsWithErrors = [];
	}

	public isSaveEnabled(): boolean {
		if (this.tempChanges) {
			return true;
		}
		if (!!this.policyEditor && this.policyEditor.HasChanges) {
			return true;
		}
		return false;
	}

	protected save(): Observable<boolean> {

		let subject = new Subject<boolean>();

		let sessionGuid = this.identityService.sessionGuid;
		let netViewGuid = this.navService.location.netViewGuid;
		let agentGuid = this.navService.location.agentGuid;
		let schedDiscGuid = this.navService.location.schedDiscGuid;
		let schedDiscRsltGuid = this.navService.location.schedDiscRsltGuid;

		this.monitorService.updateAgentMonitorPolicy(sessionGuid, netViewGuid, agentGuid, schedDiscGuid,
			schedDiscRsltGuid, this.target).subscribe(
				(result: UpdateResultDto) => {
					this.target.Modified = result.Modified;
					subject.next(true);
				},
				(error) => {
					subject.error(error);
				});

		return subject.asObservable();
	}

}
